// Fixed useActiveTimer.js - Add missing imports
import { useState, useEffect, useRef } from 'react' // Add useRef here
import { supabase } from '../lib/supabase'
import { useTenant } from '../contexts/TenantContext'
import { useAuth } from '../contexts/AuthContext'

export const useActiveTimer = () => {
  const [activeTimer, setActiveTimer] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { currentTenant } = useTenant()
  const { user } = useAuth()

  useEffect(() => {
    if (currentTenant && user) {
      fetchActiveTimer()
      
      // Set up real-time subscription with error handling
      const subscription = supabase
        .channel(`active_timers_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'active_timers',
            filter: `user_id=eq.${user.id}`,
          },
          (payload) => {
            console.log('Active timer change:', payload)
            fetchActiveTimer() // Refetch to get latest data
          }
        )
        .subscribe((status) => {
          console.log('Subscription status:', status)
        })

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [currentTenant, user])

  const fetchActiveTimer = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fix the query - remove the complex select that causes 406 error
      const { data, error: fetchError } = await supabase
        .from('active_timers')
        .select('*') // Simple select to avoid RLS issues
        .eq('user_id', user.id)
        .eq('tenant_id', currentTenant.id)
        .maybeSingle() // Use maybeSingle instead of single to avoid errors when no data

      if (fetchError) {
        console.error('Error fetching active timer:', fetchError)
        setError(fetchError.message)
        return
      }

      console.log('Active timer fetched:', data)
      setActiveTimer(data || null)
    } catch (err) {
      console.error('Error in fetchActiveTimer:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const startTimer = async (timerData) => {
    try {
      setError(null)
      
      // First, stop any existing timer
      await stopTimer()
      
      console.log('Starting timer with data:', timerData)
      
      const { data, error: insertError } = await supabase
        .from('active_timers')
        .insert([{
          ...timerData,
          user_id: user.id,
          tenant_id: currentTenant.id,
          started_at: new Date().toISOString()
        }])
        .select()
        .single()

      if (insertError) {
        console.error('Error starting timer:', insertError)
        throw insertError
      }

      console.log('Timer started successfully:', data)
      
      // Update local state immediately for better UX
      setActiveTimer(data)
      
      return { data, error: null }
    } catch (err) {
      console.error('Error in startTimer:', err)
      setError(err.message)
      return { data: null, error: err.message }
    }
  }

  const stopTimer = async () => {
    if (!activeTimer) return { error: null }

    try {
      setError(null)
      
      // Calculate duration and create time entry
      const startTime = new Date(activeTimer.started_at)
      const endTime = new Date()
      const durationHours = (endTime - startTime) / (1000 * 60 * 60)

      console.log('Stopping timer:', {
        startTime,
        endTime,
        durationHours: Math.round(durationHours * 100) / 100
      })

      // Create time entry
      const { error: entryError } = await supabase
        .from('time_entries')
        .insert([{
          user_id: user.id,
          tenant_id: currentTenant.id,
          client_id: activeTimer.client_id,
          project_id: activeTimer.project_id,
          task_name: activeTimer.task_name,
          description: activeTimer.description,
          entry_date: new Date().toISOString().split('T')[0],
          start_time: activeTimer.started_at,
          end_time: endTime.toISOString(),
          duration_hours: Math.round(durationHours * 100) / 100,
          is_billable: activeTimer.is_billable,
          status: 'draft',
        }])

      if (entryError) {
        console.error('Error creating time entry:', entryError)
        throw entryError
      }

      // Delete active timer
      const { error: deleteError } = await supabase
        .from('active_timers')  
        .delete()
        .eq('id', activeTimer.id)

      if (deleteError) {
        console.error('Error deleting active timer:', deleteError)
        throw deleteError
      }

      console.log('Timer stopped successfully')
      
      // Update local state immediately
      setActiveTimer(null)

      return { error: null }
    } catch (err) {
      console.error('Error in stopTimer:', err)
      setError(err.message)
      return { error: err.message }
    }
  }

  return {
    activeTimer,
    loading,
    error,
    startTimer,
    stopTimer,
    refetch: fetchActiveTimer
  }
}